import * as THREE from 'three';
import { useRef, useState, useEffect, forwardRef, useImperativeHandle, useMemo } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import { RigidBody, RapierRigidBody, CapsuleCollider, CuboidCollider, useRapier } from '@react-three/rapier';
import { useControls } from 'leva';
import { useStore } from '../../store/useStore';
import { CharacterPlaceholder } from './characters/CharacterPlaceholder';
import { PointerLockControls } from '@react-three/drei';
import * as Colyseus from 'colyseus.js';
import type { MyRoomState } from '../../schema/GameState';
import { HealthBar } from './ui/HealthBar';
import { useAudio } from '../../hooks/useAudio';
import { MagicSpellEffect, SwordTrailEffect } from './vfx/Effects';

import { useInput } from '../../hooks/useInput';
import { isMobile } from '../../utils/isMobile';

// Removed local useInputRef in favor of shared hook

export interface PlayerHandle {
    mesh: THREE.Group | null;
    moveTo: (point: THREE.Vector3) => void;
}

export interface PlayerProps {
    onMove?: (pos: THREE.Vector3, rx: number) => void;
    initialPos?: [number, number, number];
    room?: Colyseus.Room<MyRoomState> | null;
}

export const Player = forwardRef<PlayerHandle, PlayerProps>(({ onMove, initialPos = [0, 5, 10], room }, ref) => {
    const body = useRef<RapierRigidBody>(null);
    const visibleMesh = useRef<THREE.Group>(null);
    const grounded = useRef(false);
    const spawnSynced = useRef(false);

    // Audio & State Refs
    const stepCooldown = useRef(0);
    const lastSendTime = useRef(0);
    const prevHp = useRef(100);

    // HP State
    const [hp, setHp] = useState(100);
    const [maxHp, setMaxHp] = useState(100);
    const [animationOverride, setAnimationOverride] = useState<string | null>(null);
    const [spellEffects, setSpellEffects] = useState<Array<{ id: string; pos: THREE.Vector3; type: 'fire' | 'ice' | 'lightning'; time: number }>>([]);

    // Sync HP
    useEffect(() => {
        if (!room) return;
        const sessionPlayer = room.state.players.get(room.sessionId);
        if (sessionPlayer) {
            setHp(sessionPlayer.hp);
            setMaxHp(sessionPlayer.maxHp);
            prevHp.current = sessionPlayer.hp;
        }
    }, [room]);

    useEffect(() => {
        spawnSynced.current = false;
    }, [room]);

    // TPS Camera State
    const cameraState = useRef({ yaw: 0, pitch: 0.3 });
    const isLocked = useRef(false);
    const settingsRef = useRef({ invertY: true });

    useEffect(() => {
        const onMouseMove = (e: MouseEvent) => {
            // Check BOTH pointer lock AND pause/inventory/shop state
            const isPaused = useStore.getState().isPaused;
            const isInventoryOpen = useStore.getState().isInventoryOpen;
            const isShopOpen = useStore.getState().isShopOpen;
            if (!isLocked.current || isPaused || isInventoryOpen || isShopOpen) return;
            
            // Mouse X -> Yaw (Left/Right)
            cameraState.current.yaw -= e.movementX * 0.002;

            // Mouse Y -> Pitch (Up/Down) with limits
            // InvertY=true (Flight/Default) -> Mouse Up (-Y) -> Look Down (Pitch Increase).
            const factor = settingsRef.current.invertY ? -1 : 1;
            cameraState.current.pitch += e.movementY * 0.002 * factor;

            // STRICT CLAMP to prevent camera flipping upside down
            // 0.1 to PI/2 - 0.1 ensures cos(pitch) stays positive!
            cameraState.current.pitch = Math.max(0.1, Math.min(Math.PI / 2 - 0.1, cameraState.current.pitch));
        };

        const onLockChange = () => {
            isLocked.current = document.pointerLockElement !== null;
        };

        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('pointerlockchange', onLockChange);
        return () => {
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('pointerlockchange', onLockChange);
        };
    }, []);

    // Inputs
    const input = useInput();
    const { camera } = useThree();
    const { world, rapier } = useRapier();
    const { playSfx } = useAudio();

    // Target for Touch-to-Move
    const [targetPos, setTargetPos] = useState<THREE.Vector3 | null>(null);
    const theme = useStore((state) => state.theme);
    const gameStarted = useStore((state) => state.gameStarted);
    const isDialogActive = useStore((state) => !!state.activeDialog);
    const isInventoryOpen = useStore((state) => state.isInventoryOpen);
    const isPaused = useStore((state) => state.isPaused);
    const isShopOpen = useStore((state) => state.isShopOpen);
    const addStat = useStore((state) => state.addStat);

    // Cursor Unlock Logic
    useEffect(() => {
        if (!gameStarted || isDialogActive || isInventoryOpen || isPaused || isShopOpen) {
            if (document.pointerLockElement) {
                document.exitPointerLock();
            }
            isLocked.current = false;
        }
    }, [gameStarted, isDialogActive, isInventoryOpen, isPaused, isShopOpen]);

    useImperativeHandle(ref, () => ({
        mesh: visibleMesh.current,
        moveTo: (point: THREE.Vector3) => {
            setTargetPos(point.clone());
        }
    }));

    // Leva Controls
    const { speed, jumpForce, invertY } = useControls('Player', {
        speed: { value: 8, min: 1, max: 20 },
        jumpForce: { value: 6, min: 1, max: 15 },
        camMaxDist: { value: 30, min: 10, max: 100 },
        camDamping: { value: 0.1, min: 0.01, max: 1 },
        invertY: { value: false, label: "Invert Y Axis" }
    });

    // Update ref for event listener
    useEffect(() => {
        settingsRef.current.invertY = invertY;
    }, [invertY]);

    // Reusable Vectors
    const direction = useMemo(() => new THREE.Vector3(), []);
    const frontVector = useMemo(() => new THREE.Vector3(), []);
    const sideVector = useMemo(() => new THREE.Vector3(), []);
    const toTarget = useMemo(() => new THREE.Vector3(), []);
    const lookAtTarget = useRef<THREE.Vector3 | null>(null);
    const lastPosRef = useRef<THREE.Vector3 | null>(null);
    const distanceAccRef = useRef(0);

    // Track previous input for edge detection
    const prevInputRef = useRef(useInput());

    const playerClass = useStore((state) => state.playerClass);

    // ACTION LOGIC (Shared between Mouse & Mobile)
    const performAction = (isShift = false, isContextual = false) => {
        // Cooldown Check (Visual Cooldown is 500ms, Server is 400ms)
        const lastAttack = useStore.getState().lastAttackTime;
        if (Date.now() - lastAttack < 500) return;

        const rayOrigin = camera.position;
        const rayDir = new THREE.Vector3(0, 0, -1).applyQuaternion(camera.quaternion).normalize();

        // 1. Contextual Check (Melee/Interact)
        const maxInteractDist = 2.0; // Tight range for NPCs/shops/items
        let hit = null;
        if (rapier && world) {
            const ray = new rapier.Ray(rayOrigin, rayDir);
            hit = world.castRay(ray, maxInteractDist, true, undefined, undefined, undefined, body.current || undefined);
        }

        // Look for userData in collider OR its parent
        const getHitData = (h: any) => {
            if (!h || !h.collider) return null;
            const collider = h.collider;
            if (collider.userData) return collider.userData;
            const parent = (collider as any).parent ? (collider as any).parent() : null;
            if (parent && parent.userData) return parent.userData;
            return null;
        };

        const hitData = getHitData(hit);

        // Logic:
        // - Shift+Click = Forced Attack
        // - Click = Attack ONLY IF hitting enemy
        const isEnemyHit = hitData?.type === 'enemy';
        const shouldAttack = isShift || (isContextual && isEnemyHit);

        if (shouldAttack) {
            playSfx('attack');
            setAnimationOverride("attack");
            useStore.getState().triggerAttack(); // Update UI Cooldown

            if (playerClass === 'wizard' || playerClass === 'archer') {
                const spawnPos = body.current ? body.current.translation() : camera.position;
                const origin = new THREE.Vector3(spawnPos.x, spawnPos.y + 1.5, spawnPos.z);
                origin.add(rayDir.clone().multiplyScalar(1.2));

                // Spawn VFX effect at cast location
                const spellType = playerClass === 'wizard' ? 'fire' : 'lightning'; // Could randomize or use player choice
                const effectId = `spell-${Date.now()}`;
                setSpellEffects(prev => [...prev, { id: effectId, pos: origin.clone(), type: spellType, time: Date.now() }]);
                setTimeout(() => {
                    setSpellEffects(prev => prev.filter(e => e.id !== effectId));
                }, 500);

                // Raycast to find target point (Long distance)
                const maxRange = 150;
                let targetPoint = origin.clone().add(rayDir.clone().multiplyScalar(maxRange));
                let targetId: string | null = null;

                if (rapier && world) {
                    const r = new rapier.Ray(origin, rayDir);
                    const combatHit = world.castRay(r, maxRange, true, undefined, undefined, undefined, body.current || undefined);

                    if (combatHit) {
                        targetPoint.copy(origin).add(rayDir.clone().multiplyScalar(combatHit.timeOfImpact));

                        // Find specific enemy from the combat raycast
                        const hitCollider = combatHit.collider;
                        const userData = (hitCollider as any)?.userData || (hitCollider.parent?.() as any)?.userData;

                        if (userData?.type === 'enemy') {
                            targetId = userData.id;
                            if (room) {
                                console.log(`[Ranged Attack] Sending attack on enemy: ${targetId}`);
                                room.send("attack", { targetId });
                            }
                        }
                    }
                }

                const type = playerClass === 'wizard' ? 'magic' : 'arrow';
                const speed = playerClass === 'wizard' ? 35 : 55;
                console.log(`[${playerClass.toUpperCase()}] Spawning ${type} projectile to target:`, targetPoint);
                useStore.getState().spawnProjectile(type, origin, targetPoint, speed);
            } else {
                // Warrior Melee (Must hit enemy to register)
                if (isEnemyHit && room) {
                    const targetId = hitData?.id;
                    if (targetId) {
                        console.log(`[Melee Attack] Sending attack on enemy: ${targetId}`);
                        room.send("attack", { targetId });

                        // Spawn trail effect for sword swing
                        const trailId = `trail-${Date.now()}`;
                        setSpellEffects(prev => [...prev, { id: trailId, pos: hitData ? new THREE.Vector3(hitData.x, hitData.y, hitData.z) : camera.position, type: 'lightning', time: Date.now() }]);
                        setTimeout(() => {
                            setSpellEffects(prev => prev.filter(e => e.id !== trailId));
                        }, 200);
                    } else {
                        console.warn("[Melee Attack] Hit enemy but no ID found in userData:", hitData);
                    }
                }
            }

            setTimeout(() => setAnimationOverride(null), 500);
            return;
        }

        // Contextual Interaction
        if (isContextual && hitData && (hitData.type === 'interactable' || hitData.type === 'npc')) {
            if (hitData.onInteract && typeof hitData.onInteract === 'function') {
                hitData.onInteract();
            } else if (room && hitData.id) {
                room.send("pickup", { itemId: hitData.id });
            }
            return;
        }
    };
    // Unified attack trigger logic
    const wasAttacking = useRef(false);

    // Priority -1 ensures this runs AFTER other updates (LateUpdate)
    useFrame((_, delta) => {
        const gameStarted = useStore.getState().gameStarted;
        const isInventoryOpen = useStore.getState().isInventoryOpen;
        const isPaused = useStore.getState().isPaused;
        const isShopOpen = useStore.getState().isShopOpen;
        if (!gameStarted || isInventoryOpen || isPaused || isShopOpen) return;
        if (!body.current) return;

        if (room && !spawnSynced.current) {
            const sessionPlayer = room.state.players.get(room.sessionId);
            if (sessionPlayer) {
                body.current.setTranslation({ x: sessionPlayer.x, y: sessionPlayer.y, z: sessionPlayer.z }, true);
                body.current.setLinvel({ x: 0, y: 0, z: 0 }, true);
                spawnSynced.current = true;
            }
        }

        // --- INPUT & ACTIONS ---
        const { forward, backward, left, right, jump, attack, shift } = input;

        // TRIGGER CHECK: Fire action on transition false -> true
        if (attack && !wasAttacking.current) {
            // Check for Blocking UI
            const isBlocked = useStore.getState().activeDialog || useStore.getState().isInventoryOpen;
            if (!isBlocked) {
                // If shift held: Forced attack. If not: Contextual.
                performAction(shift, !shift);
            }
        }
        wasAttacking.current = attack;

        // Sync HP & Hit Logic
        if (room && room.state.players.has(room.sessionId)) {
            const p = room.state.players.get(room.sessionId);
            if (p) {
                if (p.hp !== hp) {
                    setHp(p.hp);
                    if (p.hp < prevHp.current) {
                        playSfx('attack'); // Being hit
                    }
                    prevHp.current = p.hp;
                }
            }
        }

        const pos = body.current.translation();
        const playerPos = new THREE.Vector3(pos.x, pos.y, pos.z);

        if (!lastPosRef.current) {
            lastPosRef.current = playerPos.clone();
        } else {
            const deltaDist = playerPos.distanceTo(lastPosRef.current);
            distanceAccRef.current += deltaDist;

            const wholeMeters = Math.floor(distanceAccRef.current);
            if (wholeMeters >= 1) {
                addStat('distance', wholeMeters);
                distanceAccRef.current -= wholeMeters;
            }

            lastPosRef.current.copy(playerPos);
        }

        // --- CAMERA FOLLOW (TPS) ---
        // Calculate offset based on yaw/pitch
        const dist = 10;
        const cx = playerPos.x + dist * Math.sin(cameraState.current.yaw) * Math.cos(cameraState.current.pitch);
        const cz = playerPos.z + dist * Math.cos(cameraState.current.yaw) * Math.cos(cameraState.current.pitch);
        const cy = playerPos.y + dist * Math.sin(cameraState.current.pitch);

        const targetCamPos = new THREE.Vector3(cx, cy, cz);

        // Frame-Independent Damping
        // lambda = 5-10 is usually good for soft follow. Higher = snappier.
        const lambda = 5;
        const alpha = 1 - Math.exp(-lambda * delta);
        camera.position.lerp(targetCamPos, alpha);

        // SMOOTH LOOKAT
        if (!lookAtTarget.current) lookAtTarget.current = new THREE.Vector3(playerPos.x, playerPos.y + 1, playerPos.z);
        const desiredLook = new THREE.Vector3(playerPos.x, playerPos.y + 1, playerPos.z);
        lookAtTarget.current.lerp(desiredLook, alpha);
        camera.lookAt(lookAtTarget.current);

        const isWASD = forward || backward || left || right;

        if (isWASD && targetPos) setTargetPos(null);

        // --- ROTATION ALIGNMENT ---
        direction.set(0, 0, 0);
        frontVector.set(0, 0, 0);
        sideVector.set(0, 0, 0);

        if (isWASD) {
            // ROBUST CAMERA-RELATIVE MOVEMENT (WYSIWYG)
            // 1. Get Camera Direction
            const camDir = new THREE.Vector3();
            camera.getWorldDirection(camDir);

            // 2. Flatten to XZ (Ground)
            camDir.y = 0;
            camDir.normalize();

            // 3. Compute Right vector (Standard: Right is -Z cross Y? No. Right is simply rotated -90 deg)
            // Forward (x, z). Right (-z, x).
            // Example: Forward (0, -1) [North]. Right (1, 0) [East].
            // x=0, z=-1. rightX = -(-1) = 1. rightZ = 0. Correct.

            const fx = camDir.x;
            const fz = camDir.z;
            const rx = -fz;
            const rz = fx;

            frontVector.set(fx, 0, fz);
            sideVector.set(rx, 0, rz);

            if (forward) direction.add(frontVector);
            if (backward) direction.sub(frontVector);
            if (right) direction.add(sideVector);
            if (left) direction.sub(sideVector);

            direction.normalize().multiplyScalar(speed);

            if (grounded.current) {
                stepCooldown.current -= delta;
                if (stepCooldown.current <= 0) {
                    playSfx('footstep', 0.5);
                    stepCooldown.current = 0.4;
                }
            }

        } else if (targetPos) {
            toTarget.subVectors(targetPos, playerPos);
            toTarget.y = 0;
            if (toTarget.length() < 1.0) setTargetPos(null);
            else direction.copy(toTarget).normalize().multiplyScalar(speed);
        }

        // --- PHYSICS APPLY ---
        const isGrounded = grounded.current;
        const vel = body.current.linvel();
        let yVelocity = vel.y;

        if (jump && isGrounded) {
            yVelocity = jumpForce;
            // playSfx('click', 0.5); // Jump sound placeholder
        }

        body.current.setLinvel({ x: direction.x, y: yVelocity, z: direction.z }, true);

        // --- ROTATION ---
        if (direction.lengthSq() > 0.1) {
            const angle = Math.atan2(direction.x, direction.z);
            if (visibleMesh.current) {
                // Smooth rotation for mesh
                // Manual frame-independent slerp-like for Y rotation
                const targetRot = angle;
                const currentRot = visibleMesh.current.rotation.y;
                let diff = targetRot - currentRot;
                while (diff > Math.PI) diff -= Math.PI * 2;
                while (diff < -Math.PI) diff += Math.PI * 2;

                const rotAlpha = 1 - Math.exp(-15 * delta); // Snappier rotation
                visibleMesh.current.rotation.y += diff * rotAlpha;
            }
        }

        // THROTTLED NETWORK SYNC (33ms)
        const now = Date.now();
        if (onMove && now - lastSendTime.current > 33) {
            const rx = visibleMesh.current ? visibleMesh.current.rotation.y : 0;
            onMove(playerPos, rx);
            lastSendTime.current = now;
        }
    }, -1); // Negative priority

    return (
        <>
            {/* Only enable PointerLock if no UI is blocking it */}
            {/* PointerLock not available / reliable on mobile — disable there */}
            {!isMobile() && gameStarted && !isDialogActive && !isInventoryOpen && !isPaused && !isShopOpen && (
                <PointerLockControls
                    selector="#gl-canvas"
                    onLock={() => isLocked.current = true}
                    onUnlock={() => isLocked.current = false}
                />
            )}
            <RigidBody
                ref={body}
                name="player"
                ccd
                colliders={false}
                friction={0}
                enabledRotations={[false, false, false]}
                position={initialPos}
                collisionGroups={0x0002FFFF} // Membership: Group 1, Mask: Hits All (0xFFFF)
            >
                <CapsuleCollider args={[0.5, 0.5]} />
                <CuboidCollider
                    args={[0.3, 0.1, 0.3]}
                    position={[0, -1.05, 0]}
                    sensor
                    onIntersectionEnter={() => grounded.current = true}
                    onIntersectionExit={() => grounded.current = false}
                />
                <group ref={visibleMesh} position={[0, -0.75, 0]}>
                    <HealthBar hp={hp} maxHp={maxHp} offset={[0, 3.3, 0]} />
                    <CharacterPlaceholder
                        theme={playerClass === 'warrior' ? theme : playerClass}
                        animation={animationOverride || (direction.lengthSq() > 0.1 ? 'run' : 'idle')}
                    />
                </group>
            </RigidBody>
            
            {/* Spell Cast VFX */}
            {spellEffects.map(effect => (
                <MagicSpellEffect
                    key={effect.id}
                    position={effect.pos}
                    spellType={effect.type}
                    scale={0.8}
                />
            ))}
        </>
    );
});
